import { useRef, useState } from 'react';
import Papa from 'papaparse';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import SportBadge from '@/components/ui/SportBadge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useThemesRef } from '@/features/reference/useReference';
import {
  importWorkouts,
  WorkoutImportReport,
  WorkoutImportReportRow,
  WorkoutImportRow,
  ImportDecision,
} from '@/api/workoutsService';
import { toaster } from '@/components/ui/toaster';

interface WorkoutImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

interface RowOverride {
  themeId?: string;
  decision?: ImportDecision;
}

const stripAccents = (value: string) =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

const pick = (row: Record<string, string>, matcher: (key: string) => boolean): string => {
  const key = Object.keys(row).find((k) => matcher(stripAccents(k)));
  return key ? (row[key] ?? '').trim() : '';
};

const mapRows = (parsed: Record<string, string>[]): WorkoutImportRow[] =>
  parsed.map((row) => ({
    sport: pick(row, (k) => k.includes('sport')),
    name: pick(row, (k) => k === 'nom' || k.includes('name') || k.includes('libelle')),
    pattern: pick(row, (k) => k.includes('nomenclature') || k.includes('pattern') || k.includes('workout')),
    theme: pick(row, (k) => k.includes('theme')),
    coachNote: pick(row, (k) => k.includes('coach') || k.includes('mot du coach')),
    note: pick(row, (k) => k === 'note' || k.includes('remarque')),
  }));

const defaultDecision = (row: WorkoutImportReportRow): ImportDecision => {
  if (row.status === 'undecoded' || row.status === 'error') return 'exclude';
  return 'import';
};

const WorkoutImportModal = ({ open, onClose, onImported }: WorkoutImportModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<WorkoutImportRow[]>([]);
  const [lineNumbers, setLineNumbers] = useState<number[]>([]);
  const [report, setReport] = useState<WorkoutImportReport | null>(null);
  const [overrides, setOverrides] = useState<Record<number, RowOverride>>({});
  const [busy, setBusy] = useState(false);
  const { data: themes = [] } = useThemesRef();

  const reset = () => {
    setFileName('');
    setRows([]);
    setLineNumbers([]);
    setReport(null);
    setOverrides({});
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const effectiveThemeId = (row: WorkoutImportReportRow): string | null =>
    overrides[row.index]?.themeId ?? row.themeId;

  const effectiveDecision = (row: WorkoutImportReportRow): ImportDecision =>
    overrides[row.index]?.decision ?? defaultDecision(row);

  const isCommittable = (row: WorkoutImportReportRow): boolean => {
    const themeId = effectiveThemeId(row);
    if (row.status === 'ok' || row.status === 'needs_theme') return Boolean(themeId);
    if (row.status === 'undecoded') return effectiveDecision(row) === 'draft' && Boolean(themeId);
    return false;
  };

  const committableCount = report ? report.rows.filter(isCommittable).length : 0;

  const setOverride = (index: number, patch: RowOverride) =>
    setOverrides((prev) => ({ ...prev, [index]: { ...prev[index], ...patch } }));

  const handleFile = (file: File) => {
    setReport(null);
    setOverrides({});
    setFileName(file.name);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const withLines = mapRows(result.data).map((row, i) => ({ row, line: i + 2 }));
        const kept = withLines.filter(({ row }) => row.sport || row.name || row.pattern);
        const mapped = kept.map((k) => k.row);
        setRows(mapped);
        setLineNumbers(kept.map((k) => k.line));
        if (mapped.length === 0) {
          toaster.create({ title: 'Aucune ligne exploitable dans le fichier', type: 'error' });
          return;
        }
        setBusy(true);
        try {
          setReport(await importWorkouts(mapped, true));
        } catch {
          toaster.create({ title: 'Analyse du fichier impossible', type: 'error' });
        } finally {
          setBusy(false);
        }
      },
      error: () => toaster.create({ title: 'Lecture du fichier impossible', type: 'error' }),
    });
  };

  const handleCommit = async () => {
    if (!report) return;
    const payload: WorkoutImportRow[] = report.rows.map((r) => ({
      ...rows[r.index],
      themeId: effectiveThemeId(r) ?? undefined,
      decision: effectiveDecision(r),
    }));
    setBusy(true);
    try {
      const result = await importWorkouts(payload, false);
      toaster.create({
        title: `${result.summary.imported} séance(s) créée(s), ${result.summary.drafted} brouillon(s)`,
        type: 'success',
      });
      onImported();
      handleClose();
    } catch {
      toaster.create({ title: 'Import impossible', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = (row: WorkoutImportReportRow) => {
    switch (row.status) {
      case 'ok':
        return <Badge color="green">décodée</Badge>;
      case 'needs_theme':
        return <Badge color="yellow">thème</Badge>;
      case 'undecoded':
        return <Badge color="red">échec</Badge>;
      default:
        return <Badge color="red">erreur</Badge>;
    }
  };

  const themePicker = (row: WorkoutImportReportRow) => (
    <select
      value={effectiveThemeId(row) ?? ''}
      onChange={(e) => setOverride(row.index, { themeId: e.target.value || undefined })}
      className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
    >
      <option value="">À choisir…</option>
      {themes.map((t) => (
        <option key={t.id} value={t.id}>
          {t.label}
        </option>
      ))}
    </select>
  );

  const themeCell = (row: WorkoutImportReportRow) => {
    if (row.status === 'ok') return <span>{row.themeLabel}</span>;
    if (row.status === 'error') return <span className="text-gray-400">—</span>;
    if (row.status === 'needs_theme') return themePicker(row);
    return effectiveDecision(row) === 'draft' ? themePicker(row) : <span className="text-gray-400">—</span>;
  };

  const decodeCell = (row: WorkoutImportReportRow) => {
    if (row.status === 'ok' || row.status === 'needs_theme') {
      return (
        <span className="text-gray-600 dark:text-gray-300">
          {row.detail}
          {row.duplicate && <span className="ml-2 text-warning-500" title="Séance déjà existante">⚠ doublon</span>}
        </span>
      );
    }
    if (row.status === 'undecoded') {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-error-500">{row.detail}</span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={effectiveDecision(row) === 'draft' ? 'primary' : 'outline'}
              onClick={() => setOverride(row.index, { decision: 'draft' })}
            >
              Brouillon
            </Button>
            <Button
              size="sm"
              variant={effectiveDecision(row) === 'exclude' ? 'primary' : 'outline'}
              onClick={() => setOverride(row.index, { decision: 'exclude' })}
            >
              Exclure
            </Button>
          </div>
        </div>
      );
    }
    return <span className="text-error-500">{row.detail}</span>;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="xl"
      title="Importer des séances (CSV)"
      footer={
        <>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button onClick={handleCommit} disabled={!report || committableCount === 0 || busy}>
            {busy ? 'Import…' : `Importer ${committableCount} séance(s)`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-gray-300 py-8 text-center text-sm text-gray-500 hover:border-brand-500 dark:border-gray-700"
        >
          <p className="font-medium text-gray-700 dark:text-gray-200">{fileName || 'Déposer le fichier .csv'}</p>
          <p>Colonnes attendues : sport, nom, nomenclature, thème, note, mot du coach (optionnels)</p>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
        </div>

        {report && (
          <>
            <div className="flex flex-wrap gap-3 text-sm">
              <Badge color="green">{report.summary.decoded} décodées</Badge>
              <Badge color="yellow">{report.summary.needsTheme} thème à choisir</Badge>
              <Badge color="red">{report.summary.undecoded} non reconnu</Badge>
              {report.summary.errors > 0 && <Badge color="red">{report.summary.errors} erreur(s)</Badge>}
              <Badge color="gray">{report.summary.total} lignes</Badge>
            </div>
            <div className="max-h-96 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-800">
              <Table>
                <THead>
                  <tr>
                    <TH>Ligne</TH>
                    <TH>Statut</TH>
                    <TH>Nom</TH>
                    <TH>Sport</TH>
                    <TH>Thème</TH>
                    <TH>Décodage</TH>
                  </tr>
                </THead>
                <TBody>
                  {report.rows.map((r) => (
                    <TR key={r.index}>
                      <TD className="text-gray-400 tabular-nums">{lineNumbers[r.index] ?? '—'}</TD>
                      <TD>{statusBadge(r)}</TD>
                      <TD>{r.name || '—'}</TD>
                      <TD>{r.sport ? <SportBadge identifier={r.sport} label={r.sportLabel ?? r.sport} /> : '—'}</TD>
                      <TD className="min-w-40">{themeCell(r)}</TD>
                      <TD>{decodeCell(r)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default WorkoutImportModal;
